import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw'; // Import rehype-raw
import rehypeSanitize from 'rehype-sanitize'; // Import rehype-sanitize
import { FaArrowLeft } from 'react-icons/fa'; // Import the icon
import { getGuideContent } from '../services/guideService';
import LoadingSpinner from '../components/LoadingSpinner';
import GuideVoting from '../components/GuideVoting';
import GuideFeedback from '../components/GuideFeedback';
import GuideHeaderInfo from '../components/GuideHeaderInfo'; // Import the new component
import '../styles/GuidePage.css';

const GuidePage = () => {
    const { guidePath: encodedGuidePath } = useParams();
    const guidePath = decodeURIComponent(encodedGuidePath);
    const location = useLocation();
    const [guide, setGuide] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchGuideContent = async () => {
            try {
                setLoading(true);
                const guideContent = await getGuideContent(guidePath);
                setGuide(guideContent);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchGuideContent();
    }, [guidePath]);

    const renderers = {
        // Handle Images
        img: ({ src, alt }) => {
            const url = new URL(src);
            const size = url.searchParams.get('size');
            let className = '';
            if (size === 'medium') {
                className = 'guide-image-medium';
            } else if (size === 'small') {
                className = 'guide-image-small';
            }
            return <img src={src} alt={alt} className={className} />;
        },
        // Handle Links
        a: ({ href, children, node }) => {
            const isActionLink = node.properties.class === 'action-link';

            // For internal links, open them in a new tab.
            if (href && href.startsWith('/')) {
                return <a href={href} target="_blank" rel="noopener noreferrer" className={isActionLink ? 'action-link' : ''}>{children}</a>;
            }

            // For external links, also open them in a new tab.
            if (href && href.startsWith('http')) {
                return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
            }

            // Handle other links (like mailto:) as standard links.
            return <a href={href}>{children}</a>;
        },
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="guide-container">
                <div className="error-message">
                    <h2>Error</h2>
                    <p>{error}</p>
                    <Link to="/" className="back-link">Go back to Home</Link>
                </div>
            </div>
        );
    }

    if (!guide) {
        return (
            <div className="guide-container">
                <div className="error-message">
                    <h2>Guide not found</h2>
                    <p>The guide you are looking for does not exist.</p>
                    <Link to="/" className="back-link">Go back to Home</Link>
                </div>
            </div>
        );
    }

    // Determine the city to link back to. Prioritize URL param, then metadata.
    const queryParams = new URLSearchParams(location.search);
    const fromCity = queryParams.get('from');
    const guideCity = fromCity || guide.metadata.city;


    return (
        <div className="guide-container">
            {guideCity ? (
                <Link to={`/city/${guideCity.toLowerCase()}`} className="back-to-map-link">
                    <FaArrowLeft /> Back to {guideCity} Map
                </Link>
            ) : (
                <Link to="/" className="back-to-map-link">
                    <FaArrowLeft /> Back to Home
                </Link>
            )}
            <header className="guide-header">
                <h1>{guide.metadata.title}</h1>
                {guide.metadata.tldr && (
                    <p className="tldr"><strong>TL;DR:</strong> {guide.metadata.tldr}</p>
                )}
                <GuideHeaderInfo guidePath={guidePath} />
            </header>
            <main className="guide-content">
                <ReactMarkdown components={renderers} rehypePlugins={[rehypeRaw, rehypeSanitize]}>
                    {guide.content}
                </ReactMarkdown>
            </main>
            <footer className="guide-footer">
                <GuideVoting guidePath={guidePath} />
                <GuideFeedback />
            </footer>
        </div>
    );
};

export default GuidePage;
